import React, { ReactElement, memo, useCallback } from "react";

// constants
import { TEXTS } from "@cart-ui/constants";

// helpers
import { AriaHidden } from "@swiggy-private/core-ui";

// test ids
import { CART_FOOTER_TEST_IDS } from "@swiggy-private/cart-test-ids";

//components
import { ActionButton, ActionsSection, Container, ContainerMeta, MetaTextWrapper, SubTitleText } from "../styled";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as LocationFilledIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/locationFilled/20";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { ADDRESS_LIST_SOURCE } from "@cart-ui/constants/Address";

export interface NewLocationFooterProps {
    addNewAddress: ({ source }: { source?: string }) => void;
    deliveryLocation: {
        address: string;
        lat: string | number;
        lng: string | number;
    };
    onChangeAddress: VoidFunction;
    showChangeAddress?: boolean;
}

function NewLocationFooter(props: NewLocationFooterProps): ReactElement {
    const { showChangeAddress = false, onChangeAddress, addNewAddress } = props;

    // Function added to handle address issues during MWEB cart migration
    const onAddAddressClick = useCallback(() => {
        addNewAddress({ source: ADDRESS_LIST_SOURCE.NEW_LOCATION_FOOTER });
    }, [addNewAddress]);

    return (
        <Container>
            <ContainerMeta>
                <AriaHidden as="div">
                    <GenericIcon Icon={LocationFilledIcon} fillColor="primary" strokeColor="primary" />
                </AriaHidden>
                <MetaTextWrapper>
                    <Text variant="V2.subheading1.neutral.black" color="text_High_Emphasis">
                        {TEXTS.YOU_ARE_IN_NEW_LOCATION}
                    </Text>
                    <SubTitleText variant="V2.body3.neutral.regular" color="text_Med_Emphasis">
                        {props.deliveryLocation.address}
                    </SubTitleText>
                </MetaTextWrapper>
            </ContainerMeta>
            {showChangeAddress ? (
                <ActionsSection>
                    <ActionButton
                        onClick={onChangeAddress}
                        data-testid={CART_FOOTER_TEST_IDS.CHANGE_ADDRESS_BTN}
                        buttonType="secondary"
                        buttonSize="large"
                        variant="fullwidth"
                    >
                        {TEXTS.SELECT_ADDRESS}
                    </ActionButton>
                    <ActionButton
                        onClick={onAddAddressClick}
                        data-testid={CART_FOOTER_TEST_IDS.ADD_NEW_ADDRESS_BTN}
                        buttonType="primary"
                        buttonSize="large"
                        variant="fullwidth"
                    >
                        {TEXTS.ADD_ADDRESS}
                    </ActionButton>
                </ActionsSection>
            ) : (
                <ActionsSection>
                    <ActionButton
                        onClick={onAddAddressClick}
                        data-testid={CART_FOOTER_TEST_IDS.ADD_ADDRESS_TO_PROCEED_BTN}
                        buttonType="primary"
                        buttonSize="large"
                        variant="fullwidth"
                    >
                        {TEXTS.ADD_ADDRESS_TO_PROCEED}
                    </ActionButton>
                </ActionsSection>
            )}
        </Container>
    );
}

export default memo(NewLocationFooter);
